import React from 'react';
import { Modal } from '@/components/ui';
import { fmt } from '@/lib/formatters';
import { CreditCard, Hash, Building2, User, FileText, Calendar, DollarSign, ArrowDownCircle } from 'lucide-react';

const PaymentDetailsModal = ({ isOpen, onClose, data }) => {
  if (!data) return null;

  const details = data.refundDetails || {};

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Refund Payment Details"
      size="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
              <ArrowDownCircle size={24} />
            </div>
            <div>
              <h3 className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-1">Total Received</h3>
              <p className="text-2xl font-black text-slate-800 tracking-tighter">₹{fmt(data.receivedAmount || 0)}</p>
            </div>
          </div>
          <div className="text-right">
            <span className="px-3 py-1 bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg shadow-emerald-200/50">
              Payment Logged
            </span>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailCard 
            icon={<Hash size={14} />} 
            label="Transaction ID" 
            value={details.transactionId || "—"} 
            isMono
          />
          <DetailCard 
            icon={<Building2 size={14} />} 
            label="Bank Name" 
            value={details.bankName || "—"} 
          />
          <DetailCard 
            icon={<CreditCard size={14} />} 
            label="Account Number" 
            value={details.accountNumber || "—"} 
          />
          <DetailCard 
            icon={<FileText size={14} />} 
            label="Receipt Reference" 
            value={details.receiptNo || "—"} 
          />
          <DetailCard 
            icon={<User size={14} />} 
            label="Receiver Name" 
            value={details.receiverName || "—"} 
          />
          <DetailCard 
            icon={<Calendar size={14} />} 
            label="Return Date" 
            value={data.date ? new Date(data.date).toLocaleDateString() : "—"} 
          />
        </div>

        {/* Bank & IFSC Section (If exists) */}
        {details.ifscCode && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Bank Identifier (IFSC)</div>
              <div className="text-xs font-mono font-bold text-brand-blue">{details.ifscCode}</div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4 border-t border-slate-100">
          <button className="btn-secondary px-8" onClick={onClose}>Close Receipt</button>
        </div>
      </div>
    </Modal>
  );
};

const DetailCard = ({ icon, label, value, isMono = false }) => (
  <div className="p-4 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-brand-blue/30 transition-colors group">
    <div className="flex items-center gap-2 mb-2">
      <div className="p-1.5 bg-slate-50 text-slate-400 rounded-lg group-hover:bg-brand-blue group-hover:text-white transition-colors">
        {icon}
      </div>
      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
    </div>
    <div className={`text-xs font-bold text-slate-700 ${isMono ? 'font-mono' : ''}`}>
      {value}
    </div>
  </div>
);

export { PaymentDetailsModal };

